import AddOutlinedIcon from "@mui/icons-material/AddCircleOutline"
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined"
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import IconButton from "@mui/material/IconButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Skeleton from "@mui/material/Skeleton"
import Tab from "@mui/material/Tab"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Tabs from "@mui/material/Tabs"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useSearchParams } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../common/components/CloseableSnackbar"
import Column from "../../../common/components/Column"
import { PaddedContentColumn } from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import PageTopSection from "../../../common/components/PageTopSection"
import { RowButColumnOnMobile } from "../../../common/components/Row"
import Row from "../../../common/components/Row"
import NoResultsTableRow from "../../../common/components/tables/NoResultsTableRow"
import ScrollableTableContainer from "../../../common/components/tables/ScrollableTableContainer"
import TablePaginationRow from "../../../common/components/tables/TablePaginationRow"
import {
  StickyHeaderTableCell,
  StickyTableCell,
} from "../../../common/components/tables/cells"
import {
  getTableSearchParams,
  usePaginationState,
  useTableSort,
} from "../../../common/components/tables/utils"
import { useAutoNavigateSearchParamState } from "../../../common/utils"
import FiltersDialog from "../../../smartsetter-utils/filters-dialog"
import { REALITY_FIELD_NAMES } from "../../../smartsetter-utils/filters-dialog/data"
import { useDialogState } from "../../../smartsetter-utils/filters-dialog/utils"
import {
  canPurchaseMls,
  canUsePowerDialer,
  isConcierge,
} from "../../users/accountRole"
import { isActiveSubscriptionStatus } from "../../users/billing"
import {
  selectProfile,
  selectProfileLoaded,
  selectSubscription,
} from "../../users/reducer"
import AssignToPodButton from "../calling/AssignToPodButton"
import StartPowerDialerButton from "../calling/StartPowerDialerButton"
import { POWER_DIALER_SOURCES } from "../calling/powerDialer"
import ApplyFiltersButton from "../components/ApplyFiltersButton"
import {
  LeadContactActionDialogs,
  useLeadContactActions,
} from "../components/LeadContactActions"
import NameWithContactActionsCell, {
  leadPhoneValue,
  stickyCheckboxCellSx,
  stickyNameCellSx,
} from "../components/NameWithContactActionsCell"
import SavedFilterTabs, {
  SAVED_FILTER_PAGES,
  useSavedFilterTabsState,
} from "../components/SavedFilterTabs"
import SectionSeparator from "../components/SectionSeparator"
import SelectMultipleTableCell, {
  useSelectMultipleTableCellState,
} from "../components/SelectMultipleTableCell"
import {
  DNC_SELECT_TOOLTIP,
  allSelectableChecked,
  dncRowSx,
  isDncLead,
  toggleSelectableRows,
} from "../components/dncLeads"
import {
  MyMlsFilterBody,
  PotentialFilterBody,
  RoleFilterBody,
  SchemaBody,
  filterFieldsFor,
} from "../components/leadFilterFields"
import {
  retrieveCRMSettings,
  selectCRMSettings,
  selectSelectedMLSID,
} from "../reducer"
import { MY_MLS_SEARCH_PARAMS } from "../settings/campaignNavigation"
import BulkListFilterBody from "./BulkListFilterBody"
import BulkSearchDialog from "./BulkSearchDialog"
import BuyMoreMlsDialog from "./BuyMoreMlsDialog"
import ConfirmAddLeadsToCandidatesDialog from "./ConfirmAddLeadsToCandidatesDialog"
import ContactSupportLink from "./ContactSupportLink"
import {
  LEAD_LIST_MLS_TABLE_COLUMN_COUNT,
  LeadListTableDataCells,
  LeadListTableHeaderCells,
  LeadListTableLoadingCells,
} from "./LeadListTableColumns"
import MlsPurchaseResultBanner from "./MlsPurchaseResultBanner"
import MlsPurchaseSuccessDialog from "./MlsPurchaseSuccessDialog"
import MyMlsPageHeader from "./MyMlsPageHeader"
import UploadDncDialog from "./UploadDncDialog"
import UploadedLeadsTab from "./UploadedLeadsTab"
import {
  buildBulkListFilter,
  serializeAgentListFilters,
} from "./agentListFilters"
import {
  boostCampaignCriteriaToAgentFilters,
  fetchBoostCampaignCriteriaFull,
  hasBoostCampaignBeenSaved,
} from "./boostCampaignCriteria"
import { mergeLeadFilterConfig } from "./conciergeCallFilterConfig"
import { normalizeLeadCallActivityFilters } from "./leadCallActivityFilters"
import {
  buildLeadsListContext,
  navigateToLeadDetails,
} from "./leadDetailsNavigation"
import { leadListOrdering, shouldIgnoreLeadRowClick } from "./leadList"
import { createLeadRatingChangeHandler } from "./leadRating"
import { useMlsAddonOffer } from "./useMlsAddonOffer"

const LEADS_TAB_SEARCH_PARAM = "tab"
const MLS_AGENTS_TAB = "mls"
const UPLOADED_LEADS_TAB = "uploaded"
const LEADS_TAB_VALUES = new Set([MLS_AGENTS_TAB, UPLOADED_LEADS_TAB])

export default function Leads() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [leads, setLeads] = useState([])
  const { checkedIDS, setCheckedIDS, checkedIDsArray, clearSelection } =
    useSelectMultipleTableCellState()
  const [isLoading, setIsLoading] = useState(false)
  const {
    sortColumn,
    sortDirection,
    serverSort,
    onSortColumnChanged,
    setSort,
  } = useTableSort("total_production", "desc", [
    "total_production",
    "total_transactions_count",
    "all_time_production",
    "all_time_transactions_count",
  ])
  const {
    pageIndex,
    setPageIndex,
    totalResultsSize,
    setTotalResultsSize,
    pageSize,
    setPageSize,
  } = usePaginationState()
  const {
    filters,
    setFilters,
    debouncedFilters,
    isFiltersDialogOpen,
    openFiltersDialog,
    closeFiltersDialog,
    handleFiltersUpdated,
    clearFilters,
  } = useDialogState()
  const savedViews = useSavedFilterTabsState(
    SAVED_FILTER_PAGES.myMLS,
    debouncedFilters,
    setFilters,
    setPageIndex,
    {
      filters,
      normalizeFilters: normalizeLeadCallActivityFilters,
      sortColumn,
      sortDirection,
      setSort,
      defaultSortColumn: "total_production",
      defaultSortDirection: "desc",
    }
  )
  const {
    candidatesTabIndex,
    savedFiltersReady,
    handleAddSavedFilterDialogClosed,
    handleCandidatesTabIndexChanged,
  } = savedViews
  const [addLeadsToCandidatesDialogOpen, setAddLeadsToCandidatesDialogOpen] =
    useState(false)
  const [buyMoreMlsDialogOpen, setBuyMoreMlsDialogOpen] = useState(false)
  const openedMlsPurchaseFromRedirectRef = useRef(false)
  const [mlsPurchaseResult, setMlsPurchaseResult] = useState(null)
  const [purchaseSuccessDialogOpen, setPurchaseSuccessDialogOpen] =
    useState(false)
  const purchaseSuccessDialogShownRef = useRef(false)
  const purchaseDialogAutoOpenedRef = useRef(false)
  const { purchaseEnabled } = useMlsAddonOffer()
  const [dncDialogOpen, setDncDialogOpen] = useState(false)
  const [bulkSearchDialogOpen, setBulkSearchDialogOpen] = useState(false)
  const [actionMenuAnchorElem, setActionMenuAnchorElem] = useState(null)
  const [listRefreshKey, setListRefreshKey] = useState(0)
  const [uploadedLeadsHeaderActions, setUploadedLeadsHeaderActions] =
    useState(null)
  const {
    isOpen: isSnackbarOpen,
    open: openSnackbar,
    close: closeSnackbar,
    message: snackbarMessage,
  } = useCloseableSnackbarState()
  const leadContactActions = useLeadContactActions(openSnackbar)
  const crmSettings = useSelector(selectCRMSettings)
  const subscription = useSelector(selectSubscription)
  const profile = useSelector(selectProfile)
  const hasPaidPlan = isActiveSubscriptionStatus(subscription?.status)
  const showPhoneColumn = isConcierge(profile)
  const tableColumnCount = LEAD_LIST_MLS_TABLE_COLUMN_COUNT
  const profileLoaded = useSelector(selectProfileLoaded)
  const mlsSelected = !!useSelector(selectSelectedMLSID)
  const showBuyMoreMlsAddon =
    mlsSelected && purchaseEnabled && canPurchaseMls(profile)
  const showPurchaseMlsButton =
    (!mlsSelected || showBuyMoreMlsAddon) && canPurchaseMls(profile)
  const selectedMlsNames = (crmSettings.settings.mlss || []).map(
    (mls) => mls.name
  )
  const dispatch = useDispatch()
  const axios = useAuthorizedAxiosClient()
  const handleLeadRatingChanged = createLeadRatingChangeHandler({
    axios,
    leadSource: "mls",
    setLeads,
    openSnackbar,
  })
  const leadFilterConfig = useMemo(
    () =>
      mergeLeadFilterConfig(
        BASE_FILTER_FIELDS,
        BASE_FIELD_NAME_TO_COMPONENT_MAP,
        profile
      ),
    [profile]
  )
  const savedBoostCriteriaBootstrappedRef = useRef(false)
  const prevFiltersKeyRef = useRef(JSON.stringify(debouncedFilters))
  const [activeLeadsTabRaw, setActiveLeadsTab] =
    useAutoNavigateSearchParamState(LEADS_TAB_SEARCH_PARAM, MLS_AGENTS_TAB)
  const activeLeadsTab = LEADS_TAB_VALUES.has(activeLeadsTabRaw)
    ? activeLeadsTabRaw
    : MLS_AGENTS_TAB

  function handleLeadsTabChanged(_event, newValue) {
    setActiveLeadsTab(newValue)
  }

  function handleViewLeadDetails(lead) {
    const rowIndex = leads.findIndex(
      (row) => String(row.id) === String(lead.id)
    )
    navigateToLeadDetails(
      navigate,
      "mls",
      lead.id,
      buildLeadsListContext({
        leadSource: "mls",
        filters: serializeAgentListFilters(debouncedFilters),
        ordering: leadListOrdering(serverSort),
        pageIndex,
        pageSize,
        rowIndex: rowIndex < 0 ? 0 : rowIndex,
        pageLeadIds: leads.map((row) => row.id),
        totalCount: totalResultsSize,
      })
    )
  }

  function handleSelectAllCandidatesCheckboxCheckChanged(e) {
    // DNC leads are never checked: the Power Dialer won't call them.
    setCheckedIDS(toggleSelectableRows(checkedIDS, leads, e.target.checked))
  }

  // A bulk search is a normal `bulk_list` filter (one at a time), so it shows in the
  // filters dialog, is stored by saved views and survives a refresh once saved.
  function handleBulkSearchApplied(outcome) {
    setFilters((current) => [
      ...current.filter(
        (filter) => filter.field !== REALITY_FIELD_NAMES.BULK_LIST
      ),
      { ...buildBulkListFilter(outcome), _complete: true },
    ])
    setPageIndex(1)
    clearSelection()
    if (candidatesTabIndex > 0) {
      // The active saved view stores this filter, so keep the list from being cleaned up.
      axios.patch(`/crm/api/agents/bulk-match/${outcome.list_id}/`, {
        pinned: true,
      })
    }
  }

  // The dialog already created the view; add its tab and open it.
  function handleBulkSearchSavedAsView(savedFilter) {
    handleAddSavedFilterDialogClosed(savedFilter)
    clearSelection()
  }

  function handleAddLeadsToCandidatesDialogClosed(importFinished) {
    if (importFinished) {
      openSnackbar("Importing leads in progress")
      setLeads(
        leads.map((lead) =>
          checkedIDS.has(lead.id) ? { ...lead, imported: true } : lead
        )
      )
      clearSelection()
    }
    setAddLeadsToCandidatesDialogOpen(false)
  }

  function handleCampaignCriteriaApply(saved) {
    const criteria = saved?.criteria ?? saved
    const campaignFilters = boostCampaignCriteriaToAgentFilters(criteria)
    setFilters(campaignFilters)
    setPageIndex(1)
    handleCandidatesTabIndexChanged(null, 0)
    savedBoostCriteriaBootstrappedRef.current = true
    openSnackbar(
      "Lead targeting submitted, synced to Airtable, and applied to My Leads"
    )
  }

  function handleCampaignCriteriaSaved() {
    openSnackbar("Lead targeting saved")
  }

  function applySavedBoostCampaignCriteria(criteria) {
    setFilters(boostCampaignCriteriaToAgentFilters(criteria))
    setPageIndex(1, false)
    handleCandidatesTabIndexChanged(null, 0)
  }

  useEffect(() => {
    const filtersKey = JSON.stringify(debouncedFilters)
    if (prevFiltersKeyRef.current === filtersKey) {
      return
    }
    prevFiltersKeyRef.current = filtersKey
    setPageIndex(1)
  }, [debouncedFilters, setPageIndex])

  useEffect(() => {
    const q = (searchParams.get("q") || "").trim()
    if (!q) return
    setFilters((current) => {
      if (
        current.some(
          (filter) =>
            filter.field === REALITY_FIELD_NAMES.NAME &&
            [].concat(filter.value ?? []).join(", ") === q
        )
      ) {
        return current
      }
      return [
        ...current,
        {
          field: REALITY_FIELD_NAMES.NAME,
          type: "contains",
          value: [q],
        },
      ]
    })
  }, [searchParams, setFilters])

  useEffect(
    () => {
      if (!profileLoaded || crmSettings.loading || !savedFiltersReady) {
        return
      }
      if (!mlsSelected) {
        setLeads([])
        setTotalResultsSize(0)
        setIsLoading(false)
        return
      }
      setIsLoading(true)
      // Changing a filter while past page 1 runs this effect twice - once with
      // the stale pageIndex, then again after the reset effect sets it to 1 -
      // and both responses used to write to state unconditionally, so whichever
      // landed last won and the table desynced from the paginator. `cancelled`
      // makes the superseded request a no-op.
      let cancelled = false
      axios
        .post(
          `/crm/api/agents/`,
          {
            filters: serializeAgentListFilters(debouncedFilters),
          },
          {
            params: getTableSearchParams(
              null,
              pageIndex,
              pageSize,
              leadListOrdering(serverSort)
            ),
          }
        )
        .then(({ data }) => {
          if (cancelled) {
            return
          }
          setLeads(data.results)
          setTotalResultsSize(data.count)
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false)
          }
        })
      return () => {
        cancelled = true
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      profileLoaded,
      crmSettings,
      savedFiltersReady,
      mlsSelected,
      serverSort,
      pageIndex,
      pageSize,
      debouncedFilters,
      listRefreshKey,
    ]
  )

  useEffect(() => {
    if (searchParams.get(MY_MLS_SEARCH_PARAMS.openMlsPurchase) !== "1") {
      return
    }
    if (openedMlsPurchaseFromRedirectRef.current) {
      return
    }
    openedMlsPurchaseFromRedirectRef.current = true
    const next = new URLSearchParams(searchParams)
    next.delete(MY_MLS_SEARCH_PARAMS.openMlsPurchase)
    setSearchParams(next, { replace: true })
    setBuyMoreMlsDialogOpen(true)
  }, [searchParams, setSearchParams])

  // The purchase popup is closed by default. It opens by itself only once the
  // settings were fetched successfully and show no MLS, never while they are
  // still loading or after a failed fetch (that used to flash the popup for
  // users who already own an MLS).
  useEffect(() => {
    if (
      !profileLoaded ||
      crmSettings.loading ||
      !crmSettings.loaded ||
      mlsSelected ||
      !canPurchaseMls(profile)
    ) {
      return
    }
    purchaseDialogAutoOpenedRef.current = true
    setBuyMoreMlsDialogOpen(true)
  }, [
    profileLoaded,
    crmSettings.loading,
    crmSettings.loaded,
    mlsSelected,
    profile,
  ])

  // An MLS showed up (e.g. a purchase finished being assigned) while the popup
  // we opened was still showing: close it.
  useEffect(() => {
    if (mlsSelected && purchaseDialogAutoOpenedRef.current) {
      purchaseDialogAutoOpenedRef.current = false
      setBuyMoreMlsDialogOpen(false)
    }
  }, [mlsSelected])

  function handleBuyMoreMlsDialogClosed() {
    purchaseDialogAutoOpenedRef.current = false
    setBuyMoreMlsDialogOpen(false)
  }

  useEffect(
    () => {
      if (
        !profileLoaded ||
        !mlsSelected ||
        crmSettings.loading ||
        savedBoostCriteriaBootstrappedRef.current
      ) {
        return
      }
      fetchBoostCampaignCriteriaFull(axios).then(({ criteria, updatedAt }) => {
        if (!hasBoostCampaignBeenSaved({ criteria, updatedAt })) {
          return
        }
        savedBoostCriteriaBootstrappedRef.current = true
        applySavedBoostCampaignCriteria(criteria)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileLoaded, mlsSelected, crmSettings.loading]
  )

  useEffect(
    () => {
      const purchaseId = searchParams.get("purchase_id")
      if (searchParams.get("mls_purchase") !== "success" || !purchaseId) {
        return undefined
      }

      let cancelled = false
      let attempts = 0

      const poll = () => {
        axios
          .get(`/billing/api/mls-purchases/${purchaseId}/`)
          .then(({ data }) => {
            if (cancelled) {
              return
            }
            setMlsPurchaseResult(data)
            if (!purchaseSuccessDialogShownRef.current) {
              purchaseSuccessDialogShownRef.current = true
              setPurchaseSuccessDialogOpen(true)
            }
            if (data.assignment_status === "assigned") {
              dispatch(retrieveCRMSettings())
            }
            if (
              data.assignment_status === "assigned" ||
              data.assignment_status === "failed" ||
              attempts >= 40
            ) {
              return
            }
            attempts += 1
            setTimeout(poll, 5000)
          })
          .catch(() => {
            // Re-arm on failure too. A single transient error (a 502, a dropped
            // connection, a sleep/wake) used to end the poll for good, leaving
            // the banner frozen on "pending" after the MLS was assigned.
            if (cancelled || attempts >= 40) {
              return
            }
            attempts += 1
            setTimeout(poll, 5000)
          })
      }

      poll()
      return () => {
        cancelled = true
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams]
  )

  function dismissMlsPurchaseBanner() {
    setMlsPurchaseResult(null)
    const next = new URLSearchParams(searchParams)
    next.delete("mls_purchase")
    next.delete("purchase_id")
    setSearchParams(next)
  }

  return (
    <Column>
      <PageTopSection
        title={
          <Row sx={{ alignItems: "center", gap: 1 }}>
            <Typography variant="h2">
              {activeLeadsTab === UPLOADED_LEADS_TAB
                ? "Import Leads"
                : "My Leads"}
            </Typography>
            {activeLeadsTab === MLS_AGENTS_TAB &&
            (crmSettings.settings.mlss || []).length > 0 ? (
              <Tooltip
                placement="right"
                title={
                  <>
                    You selected{" "}
                    {(crmSettings.settings.mlss || [])
                      .map((mls) => mls.name)
                      .join(", ")}{" "}
                    MLS.
                    {showBuyMoreMlsAddon
                      ? " Purchase additional MLS with Purchase MLS, or "
                      : " To add or remove MLS, ask your account owner or "}
                    <ContactSupportLink color="info.light" />
                    {showBuyMoreMlsAddon ? " to remove an add-on." : "."}
                  </>
                }
              >
                <InfoOutlinedIcon
                  variant="g500"
                  sx={{ cursor: "pointer", fontSize: "18px" }}
                />
              </Tooltip>
            ) : null}
          </Row>
        }
      >
        {activeLeadsTab === MLS_AGENTS_TAB ? (
          <RowButColumnOnMobile
            sx={{
              alignItems: { xs: "flex-end", sm: "center" },
              rowGap: 1,
              columnGap: 2,
            }}
          >
            {mlsSelected ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddOutlinedIcon />}
                onClick={() => setAddLeadsToCandidatesDialogOpen(true)}
                disabled={checkedIDS.size === 0}
              >
                Add to Candidates
              </Button>
            ) : null}
            <ApplyFiltersButton
              filters={filters}
              openFiltersDialog={openFiltersDialog}
            />
            {hasPaidPlan && canUsePowerDialer(profile) ? (
              <>
                <StartPowerDialerButton
                  source={POWER_DIALER_SOURCES.mls}
                  filters={serializeAgentListFilters(debouncedFilters)}
                  ordering={serverSort}
                  selectedIds={checkedIDsArray}
                  disabled={!mlsSelected}
                  disabledReason="Purchase MLS access before starting Power Dialer."
                />
                <AssignToPodButton
                  source={POWER_DIALER_SOURCES.mls}
                  filters={serializeAgentListFilters(debouncedFilters)}
                  ordering={serverSort}
                  selectedIds={checkedIDsArray}
                  disabled={!mlsSelected}
                />
              </>
            ) : null}
            <IconButton
              onClick={(e) => setActionMenuAnchorElem(e.currentTarget)}
              aria-label="Options"
              disabled={!mlsSelected}
            >
              <MoreVertOutlinedIcon />
            </IconButton>
            <Menu
              open={Boolean(actionMenuAnchorElem)}
              anchorEl={actionMenuAnchorElem}
              onClose={() => setActionMenuAnchorElem(null)}
            >
              <MenuItem
                onClick={() => {
                  setActionMenuAnchorElem(null)
                  setBulkSearchDialogOpen(true)
                }}
              >
                <ListItemIcon>
                  <ManageSearchOutlinedIcon color="primary" />
                </ListItemIcon>
                <ListItemText>Bulk search</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setActionMenuAnchorElem(null)
                  setDncDialogOpen(true)
                }}
              >
                <ListItemIcon>
                  <CloudUploadOutlinedIcon color="error" />
                </ListItemIcon>
                <ListItemText>Upload DNC List</ListItemText>
              </MenuItem>
            </Menu>
            <ConfirmAddLeadsToCandidatesDialog
              open={addLeadsToCandidatesDialogOpen}
              onClose={handleAddLeadsToCandidatesDialogClosed}
              selectedLeadIDS={checkedIDsArray}
            />
            <FiltersDialog
              open={isFiltersDialogOpen}
              onClosed={closeFiltersDialog}
              parentFilters={filters}
              onFiltersUpdated={handleFiltersUpdated}
              onFiltersCleared={clearFilters}
              filterTypes={leadFilterConfig.filterFields.filter(
                (field) =>
                  showPhoneColumn ||
                  field.fieldName !== REALITY_FIELD_NAMES.PHONE
              )}
              fieldNameToComponentMap={leadFilterConfig.bodyMap}
              footerNote={
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Searching for many agents at once?{" "}
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      closeFiltersDialog()
                      setBulkSearchDialogOpen(true)
                    }}
                    sx={{ p: 0, minWidth: "auto", verticalAlign: "baseline" }}
                  >
                    Try Bulk search
                  </Button>
                </Typography>
              }
            />
          </RowButColumnOnMobile>
        ) : activeLeadsTab === UPLOADED_LEADS_TAB ? (
          uploadedLeadsHeaderActions
        ) : null}
      </PageTopSection>
      <PaddedContentColumn sx={{ pt: 2, pb: 0 }}>
        <Tabs
          value={activeLeadsTab}
          onChange={handleLeadsTabChanged}
          centered
          variant="standard"
          sx={{
            mb: 1,
            minHeight: 40,
            "& .MuiTabs-flexContainer": {
              justifyContent: "center",
            },
          }}
        >
          <Tab label="MLS Agents" value={MLS_AGENTS_TAB} />
          <Tab label="Import Leads" value={UPLOADED_LEADS_TAB} />
        </Tabs>
      </PaddedContentColumn>
      {activeLeadsTab === MLS_AGENTS_TAB ? (
        <PaddedContentColumn sx={{ pt: 0 }}>
          <SectionSeparator sx={{ mt: 0 }}>
            <MlsPurchaseResultBanner
              purchase={mlsPurchaseResult}
              onDismiss={dismissMlsPurchaseBanner}
            />
            <MyMlsPageHeader
              mlsNames={selectedMlsNames}
              mlsSelected={mlsSelected}
              onBuyMoreMlsClick={() => setBuyMoreMlsDialogOpen(true)}
              showPurchaseMlsButton={showPurchaseMlsButton}
              onCampaignCriteriaApply={handleCampaignCriteriaApply}
              onCampaignCriteriaSaved={handleCampaignCriteriaSaved}
            />
            <SavedFilterTabs {...savedViews} />
            <ConditionalPurpleLinearProgress
              visible={isLoading || crmSettings.loading}
            />
            <ScrollableTableContainer
              sx={{
                maxHeight: {
                  xs: "calc(100vh - 220px)",
                  sm: "calc(100vh - 430px)",
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <StickyHeaderTableCell sx={stickyCheckboxCellSx}>
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={
                          !isLoading && allSelectableChecked(checkedIDS, leads)
                        }
                        onChange={handleSelectAllCandidatesCheckboxCheckChanged}
                      />
                    </StickyHeaderTableCell>
                    <LeadListTableHeaderCells
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSortColumnChanged={onSortColumnChanged}
                      stickyNameCellSx={stickyNameCellSx}
                      includeAllTimeProductionColumns
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 20 }).map((_, index) => (
                      <TableRow key={index}>
                        <StickyTableCell sx={stickyCheckboxCellSx}>
                          <Skeleton
                            variant="rectangular"
                            width={24}
                            height={24}
                          />
                        </StickyTableCell>
                        <StickyTableCell sx={stickyNameCellSx}>
                          <Skeleton variant="text" width={180} />
                        </StickyTableCell>
                        <LeadListTableLoadingCells
                          includeAllTimeProductionColumns
                        />
                      </TableRow>
                    ))
                  ) : !mlsSelected ? (
                    <TableRow>
                      <StickyTableCell
                        colSpan={tableColumnCount}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Column sx={{ alignItems: "center", gap: 1.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {showPurchaseMlsButton
                              ? "Purchase MLS access to browse agents in your market."
                              : "No MLS active. Ask your account owner to purchase MLS access."}
                          </Typography>
                          {showPurchaseMlsButton ? (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => setBuyMoreMlsDialogOpen(true)}
                            >
                              Purchase MLS
                            </Button>
                          ) : null}
                        </Column>
                      </StickyTableCell>
                    </TableRow>
                  ) : leads.length === 0 ? (
                    <NoResultsTableRow colSpan={tableColumnCount} />
                  ) : (
                    leads.map((lead, index) => (
                      <TableRow
                        key={lead.id}
                        onClick={(e) => {
                          if (shouldIgnoreLeadRowClick(e)) {
                            return
                          }
                          handleViewLeadDetails(lead)
                        }}
                        sx={{
                          opacity: lead.imported ? 0.5 : 1,
                          cursor: "pointer",
                          ...dncRowSx(lead),
                        }}
                      >
                        <SelectMultipleTableCell
                          checkedIDs={checkedIDS}
                          setCheckedIDS={setCheckedIDS}
                          row={lead}
                          rows={leads}
                          index={index}
                          disabled={lead.imported || isDncLead(lead)}
                          disabledTooltip={
                            isDncLead(lead) ? DNC_SELECT_TOOLTIP : undefined
                          }
                          CellComponent={StickyTableCell}
                          cellProps={{ sx: stickyCheckboxCellSx }}
                        />
                        <NameWithContactActionsCell
                          name={lead.name}
                          title={lead.name}
                          phone={leadPhoneValue(lead)}
                          email={lead.email}
                          isDnc={Boolean(lead.is_dnc)}
                          showActions={hasPaidPlan}
                          onCall={(e) =>
                            leadContactActions.handleCallButtonClicked(e, lead)
                          }
                          onSMS={(e) =>
                            leadContactActions.handleSMSButtonClicked(e, lead)
                          }
                          onEmail={(e) =>
                            leadContactActions.handleEmailButtonClicked(e, lead)
                          }
                        />
                        <LeadListTableDataCells
                          lead={lead}
                          includeAllTimeProductionColumns
                          onRatingChanged={handleLeadRatingChanged}
                        />
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
            <TablePaginationRow
              pageIndex={pageIndex}
              setPageIndex={setPageIndex}
              pageSize={pageSize}
              setPageSize={setPageSize}
              tableRows={leads}
              totalResultsSize={totalResultsSize}
              paginationDisabled={!mlsSelected}
            />
          </SectionSeparator>
        </PaddedContentColumn>
      ) : (
        <PaddedContentColumn sx={{ pt: 0 }}>
          <UploadedLeadsTab
            onHeaderActionsChange={setUploadedLeadsHeaderActions}
          />
        </PaddedContentColumn>
      )}
      <BulkSearchDialog
        open={bulkSearchDialogOpen}
        onClose={() => setBulkSearchDialogOpen(false)}
        onApply={handleBulkSearchApplied}
        onSaveAsView={handleBulkSearchSavedAsView}
        savedViewPage={SAVED_FILTER_PAGES.myMLS}
      />
      <UploadDncDialog
        open={dncDialogOpen}
        uploadUrl="/crm/api/agents/upload-dnc/"
        onClose={() => setDncDialogOpen(false)}
        onSuccess={(data) => {
          setDncDialogOpen(false)
          openSnackbar(data?.message || "DNC list uploaded successfully.")
          setListRefreshKey((key) => key + 1)
        }}
      />
      <BuyMoreMlsDialog
        open={buyMoreMlsDialogOpen}
        onClose={handleBuyMoreMlsDialogClosed}
      />
      <MlsPurchaseSuccessDialog
        open={purchaseSuccessDialogOpen}
        purchase={mlsPurchaseResult}
        onClose={() => setPurchaseSuccessDialogOpen(false)}
      />
      <LeadContactActionDialogs
        actionsState={leadContactActions}
        openSnackbar={openSnackbar}
      />
      <CloseableSnackbar
        open={isSnackbarOpen}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
    </Column>
  )
}

const BASE_FILTER_FIELDS = filterFieldsFor(SAVED_FILTER_PAGES.myMLS, [
  REALITY_FIELD_NAMES.NAME,
  REALITY_FIELD_NAMES.MLS,
  REALITY_FIELD_NAMES.ANNUAL_VALUE,
  REALITY_FIELD_NAMES.POTENTIAL_TIER,
  REALITY_FIELD_NAMES.ROLE,
  REALITY_FIELD_NAMES.EMAIL,
  REALITY_FIELD_NAMES.PHONE,
  REALITY_FIELD_NAMES.HAS_CALLABLE_PHONE,
  REALITY_FIELD_NAMES.CITY,
  REALITY_FIELD_NAMES.STATE,
  REALITY_FIELD_NAMES.ZIPCODE,
  REALITY_FIELD_NAMES.SALES_VOLUME,
  REALITY_FIELD_NAMES.NO_OF_SALES,
  REALITY_FIELD_NAMES.ALL_TIME_SALES_VOLUME,
  REALITY_FIELD_NAMES.ALL_TIME_NO_OF_SALES,
  REALITY_FIELD_NAMES.YEARS_IN_BUSINESS,
  REALITY_FIELD_NAMES.OFFICE_NAME,
  REALITY_FIELD_NAMES.CUSTOM_TAG,
  REALITY_FIELD_NAMES.IS_DNC,
  REALITY_FIELD_NAMES.BULK_LIST,
])

const BASE_FIELD_NAME_TO_COMPONENT_MAP = {
  [REALITY_FIELD_NAMES.NAME]: SchemaBody,
  [REALITY_FIELD_NAMES.MLS]: MyMlsFilterBody,
  [REALITY_FIELD_NAMES.ANNUAL_VALUE]: SchemaBody,
  [REALITY_FIELD_NAMES.POTENTIAL_TIER]: PotentialFilterBody,
  [REALITY_FIELD_NAMES.ROLE]: RoleFilterBody,
  [REALITY_FIELD_NAMES.NO_OF_SALES]: SchemaBody,
  [REALITY_FIELD_NAMES.SALES_VOLUME]: SchemaBody,
  [REALITY_FIELD_NAMES.ALL_TIME_NO_OF_SALES]: SchemaBody,
  [REALITY_FIELD_NAMES.ALL_TIME_SALES_VOLUME]: SchemaBody,
  [REALITY_FIELD_NAMES.CITY]: SchemaBody,
  [REALITY_FIELD_NAMES.EMAIL]: SchemaBody,
  [REALITY_FIELD_NAMES.PHONE]: SchemaBody,
  [REALITY_FIELD_NAMES.HAS_CALLABLE_PHONE]: SchemaBody,
  [REALITY_FIELD_NAMES.OFFICE_NAME]: SchemaBody,
  [REALITY_FIELD_NAMES.STATE]: SchemaBody,
  [REALITY_FIELD_NAMES.ZIPCODE]: SchemaBody,
  [REALITY_FIELD_NAMES.YEARS_IN_BUSINESS]: SchemaBody,
  [REALITY_FIELD_NAMES.CUSTOM_TAG]: SchemaBody,
  [REALITY_FIELD_NAMES.IS_DNC]: SchemaBody,
  [REALITY_FIELD_NAMES.BULK_LIST]: BulkListFilterBody,
}
